// src/modules/communications/components/SoftphoneWidget.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useSoftphone } from '../context/SoftphoneContext';
import { searchLeads } from '../services/softphoneService';
import {
  FaPhone, FaPhoneSlash, FaMicrophone, FaMicrophoneSlash,
  FaTh, FaChevronUp, FaSearch, FaUser,
  FaTimes, FaHeadset, FaVolumeUp
} from 'react-icons/fa';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(132, 201, 89, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(132, 201, 89, 0); }
  100% { box-shadow: 0 0 0 0 rgba(132, 201, 89, 0); }
`;

// Lives inline in the header bar, next to the notification bell.
const WidgetContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.typography.fontFamily};
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$inCall ? '#84c959' : props.theme.colors.textPrimary};
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${props => props.theme.spacing(1)};

  &:hover { color: ${props => props.$inCall ? '#84c959' : props.theme.colors.primary}; }
`;

const TriggerIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const TriggerTimer = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #84c959;

  @media (max-width: 480px) { display: none; }
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'registered': return '#84c959';
      case 'registering': return '#F69620';
      case 'error': return '#FF5252';
      default: return '#787878';
    }
  }};
  ${props => props.status === 'registered' && css`animation: ${pulse} 2s infinite;`}
`;

const TriggerDot = styled(StatusDot)`
  position: absolute;
  right: -3px;
  bottom: -1px;
  width: 8px;
  height: 8px;
  box-sizing: content-box;
  border: 2px solid ${props => props.theme.colors.surface};
`;

const Panel = styled.div`
  position: fixed;
  top: 64px;
  right: 8px;
  width: 330px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 76px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  z-index: 1002;

  /* Stay clear of the mobile bottom navigation */
  @media (max-width: 768px) {
    max-height: calc(100vh - 150px);
  }

  @media (max-width: 420px) {
    left: 8px;
    right: 8px;
    width: auto;
  }
`;

const PanelStickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid #444;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.textPrimary};
  font-weight: 500;
  font-size: 0.95rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 4px;
`;

const HeaderBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;

  &:hover { color: ${props => props.theme.colors.textPrimary}; }
`;

const PanelBody = styled.div`
  padding: 16px;
`;

// Search
const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #555;
  border-radius: 8px;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  outline: none;
  box-sizing: border-box;

  &:focus { border-color: ${props => props.theme.colors.primary}; }
  &::placeholder { color: ${props => props.theme.colors.inputGray}; }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.inputGray};
  font-size: 0.85rem;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.background};
  border: 1px solid #555;
  border-radius: 8px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 10;
  margin-top: 4px;
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.85rem;

  &:hover { background: ${props => props.theme.colors.surface}; }
`;

const LeadAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}33;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

const LeadInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeadName = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LeadPhone = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
`;

// Number display
const NumberDisplay = styled.div`
  text-align: center;
  padding: 8px 0 12px;
  font-size: 1.4rem;
  font-weight: 300;
  color: ${props => props.theme.colors.textPrimary};
  letter-spacing: 1px;
  min-height: 32px;
`;

// Dial pad
const DialPad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const DialKey = styled.button`
  height: 48px;
  border: none;
  border-radius: 12px;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: #3a3a3a; }
  &:active { background: ${props => props.theme.colors.primary}44; }
`;

const DialKeyLetters = styled.span`
  font-size: 0.55rem;
  color: ${props => props.theme.colors.textSecondary};
  letter-spacing: 2px;
  margin-top: -2px;
`;

const CallButton = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 12px;
  background: ${props => props.danger ? '#FF5252' : '#84c959'};
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Active call
const ActiveCallContainer = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const CallerName = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const CallerNumber = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 4px;
`;

const CallTimer = styled.div`
  font-size: 1.8rem;
  font-weight: 300;
  color: ${props => props.theme.colors.primary};
  margin: 16px 0;
  font-variant-numeric: tabular-nums;
`;

const CallControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 20px 0 8px;
`;

const ControlButton = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.background};
  color: ${props => props.active ? '#000' : props.theme.colors.textPrimary};
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover { opacity: 0.85; }
`;

const HangupButton = styled(ControlButton)`
  width: 60px;
  height: 60px;
  background: #FF5252;
  color: white;
  font-size: 1.3rem;

  &:hover { background: #e04444; }
`;

const ErrorBanner = styled.div`
  background: #FF525233;
  color: #FF5252;
  padding: 8px 12px;
  font-size: 0.8rem;
  text-align: center;
  cursor: pointer;
`;

const AudioDeviceSelect = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #444;
`;

const DeviceSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;

  select {
    flex: 1;
    padding: 4px 8px;
    border: 1px solid #555;
    border-radius: 4px;
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
    font-size: 0.8rem;
    outline: none;
  }
`;

const DIAL_KEYS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const formatPhoneDisplay = (number) => {
  if (!number) return '';
  const digits = number.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return number;
};

const SoftphoneWidget = () => {
  const {
    deviceState, callState, callDirection, callerInfo,
    isMuted, callDuration, error,
    isWidgetExpanded, setIsWidgetExpanded,
    makeCall, hangupCall, toggleMute, sendDTMF, setError,
    audioInputDevices, audioOutputDevices,
    selectAudioInput, selectAudioOutput,
  } = useSoftphone();

  const [dialNumber, setDialNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const wasInCallRef = useRef(false);
  const autoOpenedRef = useRef(false);
  const expandedRef = useRef(isWidgetExpanded);
  expandedRef.current = isWidgetExpanded;

  const isInCall = callState === 'connected' || callState === 'connecting';

  // A call starting (including one answered from the incoming overlay) pops the
  // panel open; when it ends we only re-collapse what we opened ourselves. Only
  // the transitions matter — minimizing mid-call must stay minimized.
  useEffect(() => {
    if (isInCall === wasInCallRef.current) return;
    wasInCallRef.current = isInCall;

    if (isInCall) {
      autoOpenedRef.current = !expandedRef.current;
      setIsWidgetExpanded(true);
    } else if (autoOpenedRef.current) {
      autoOpenedRef.current = false;
      setIsWidgetExpanded(false);
    }
  }, [isInCall, setIsWidgetExpanded]);

  // Close the panel when clicking anywhere outside it (matches the header's
  // notification dropdown). The trigger button keeps showing call state.
  useEffect(() => {
    if (!isWidgetExpanded) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsWidgetExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWidgetExpanded, setIsWidgetExpanded]);

  // Search leads as user types
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchLeads(searchQuery);
        const leads = results.leads || results.rows || results || [];
        setSearchResults(Array.isArray(leads) ? leads.slice(0, 8) : []);
        setShowSearch(true);
      } catch (e) {
        setSearchResults([]);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handleSelectLead = useCallback((lead) => {
    setSelectedLead(lead);
    setDialNumber(lead.phone || '');
    setSearchQuery('');
    setShowSearch(false);
  }, []);

  const handleDial = useCallback((digit) => {
    if (callState === 'connected' || callState === 'connecting') {
      sendDTMF(digit);
    } else {
      setDialNumber(prev => prev + digit);
    }
  }, [callState, sendDTMF]);

  const handleBackspace = useCallback(() => {
    setDialNumber(prev => prev.slice(0, -1));
  }, []);

  const handleMakeCall = useCallback(() => {
    if (!dialNumber) return;
    // Ensure E.164 format
    let number = dialNumber.replace(/\D/g, '');
    if (number.length === 10) number = '+1' + number;
    else if (number.length === 11 && number.startsWith('1')) number = '+' + number;
    else if (!number.startsWith('+')) number = '+' + number;

    makeCall(number, selectedLead?.id || null);
    setIsWidgetExpanded(true);
  }, [dialNumber, selectedLead, makeCall, setIsWidgetExpanded]);

  const handleHangup = useCallback(() => {
    hangupCall();
    setDialNumber('');
    setSelectedLead(null);
    setShowKeypad(false);
  }, [hangupCall]);

  // Keyboard support — capture digit keys only while the panel is open, so
  // typing elsewhere in the CRM is never swallowed by the dial pad.
  useEffect(() => {
    if (!isWidgetExpanded) return;

    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const key = e.key;
      const validDigits = ['0','1','2','3','4','5','6','7','8','9','*','#'];

      if (validDigits.includes(key)) {
        e.preventDefault();
        handleDial(key);
      } else if (key === 'Backspace' && callState === 'idle') {
        e.preventDefault();
        handleBackspace();
      } else if (key === 'Enter' && callState === 'idle' && dialNumber) {
        e.preventDefault();
        handleMakeCall();
      } else if (key === 'Escape') {
        setIsWidgetExpanded(false);
      } else if (key === 'm' && (callState === 'connected' || callState === 'connecting')) {
        e.preventDefault();
        toggleMute();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isWidgetExpanded, callState, dialNumber, handleDial, handleBackspace, handleMakeCall, setIsWidgetExpanded, toggleMute]);

  // Don't render if not authenticated
  const isAuthenticated = !!localStorage.getItem('token');
  if (!isAuthenticated) return null;

  return (
    <WidgetContainer ref={containerRef}>
      <TriggerButton
        onClick={() => setIsWidgetExpanded(!isWidgetExpanded)}
        $inCall={isInCall}
        title={isInCall ? 'Call in progress' : 'Softphone'}
        aria-label="Softphone"
        aria-expanded={isWidgetExpanded}
      >
        <TriggerIcon>
          <FaHeadset />
          <TriggerDot status={isInCall ? 'registered' : deviceState} />
        </TriggerIcon>
        {isInCall && <TriggerTimer>{formatDuration(callDuration)}</TriggerTimer>}
      </TriggerButton>

      {isWidgetExpanded && (
      <Panel>
        <PanelStickyHeader>
        <PanelHeader>
          <HeaderLeft>
            <StatusDot status={deviceState} />
            <FaHeadset />
            Softphone
          </HeaderLeft>
          <HeaderActions>
            {!isInCall && (
              <HeaderBtn onClick={() => setShowAudioSettings(!showAudioSettings)} title="Audio settings">
                <FaVolumeUp />
              </HeaderBtn>
            )}
            <HeaderBtn onClick={() => setIsWidgetExpanded(false)} title="Minimize">
              <FaChevronUp />
            </HeaderBtn>
          </HeaderActions>
        </PanelHeader>

        {error && (
          <ErrorBanner onClick={() => setError(null)}>
            {error} (click to dismiss)
          </ErrorBanner>
        )}
        </PanelStickyHeader>

        <PanelBody>
          {/* IDLE / DIALING VIEW */}
          {callState === 'idle' && (
            <>
              <SearchContainer>
                <SearchIcon><FaSearch /></SearchIcon>
                <SearchInput
                  placeholder="Search leads or type number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearch(true)}
                  onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                />
                {showSearch && searchResults.length > 0 && (
                  <SearchResults>
                    {searchResults.map((lead) => (
                      <SearchResultItem key={lead.id} onMouseDown={() => handleSelectLead(lead)}>
                        <LeadAvatar><FaUser /></LeadAvatar>
                        <LeadInfo>
                          <LeadName>{lead.name}</LeadName>
                          <LeadPhone>{formatPhoneDisplay(lead.phone)}</LeadPhone>
                        </LeadInfo>
                      </SearchResultItem>
                    ))}
                  </SearchResults>
                )}
              </SearchContainer>

              {selectedLead && (
                <SearchResultItem as="div" style={{ background: '#F6962022', borderRadius: 8, marginBottom: 8, padding: '8px 12px' }}>
                  <LeadAvatar><FaUser /></LeadAvatar>
                  <LeadInfo>
                    <LeadName>{selectedLead.name}</LeadName>
                    <LeadPhone>{formatPhoneDisplay(selectedLead.phone)}</LeadPhone>
                  </LeadInfo>
                  <HeaderBtn onClick={() => { setSelectedLead(null); setDialNumber(''); }}>
                    <FaTimes />
                  </HeaderBtn>
                </SearchResultItem>
              )}

              <NumberDisplay
                onClick={() => {
                  if (dialNumber.length > 0) handleBackspace();
                }}
                style={{ cursor: dialNumber ? 'pointer' : 'default' }}
              >
                {formatPhoneDisplay(dialNumber) || '\u00A0'}
              </NumberDisplay>

              <DialPad>
                {DIAL_KEYS.map(({ digit, letters }) => (
                  <DialKey key={digit} onClick={() => handleDial(digit)}>
                    {digit}
                    {letters && <DialKeyLetters>{letters}</DialKeyLetters>}
                  </DialKey>
                ))}
              </DialPad>

              <CallButton
                onClick={handleMakeCall}
                disabled={!dialNumber || deviceState !== 'registered'}
              >
                <FaPhone /> Call
              </CallButton>

              {showAudioSettings && (audioInputDevices.length > 0 || audioOutputDevices.length > 0) && (
                <AudioDeviceSelect>
                  {audioInputDevices.length > 0 && (
                    <DeviceSelectRow>
                      <FaMicrophone />
                      <select onChange={(e) => selectAudioInput(e.target.value)}>
                        {audioInputDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>
                        ))}
                      </select>
                    </DeviceSelectRow>
                  )}
                  {audioOutputDevices.length > 0 && (
                    <DeviceSelectRow>
                      <FaVolumeUp />
                      <select onChange={(e) => selectAudioOutput(e.target.value)}>
                        {audioOutputDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>
                        ))}
                      </select>
                    </DeviceSelectRow>
                  )}
                </AudioDeviceSelect>
              )}
            </>
          )}

          {/* ACTIVE CALL VIEW */}
          {isInCall && (
            <ActiveCallContainer>
              <CallerName>
                {callerInfo?.name || (callDirection === 'outbound' ? 'Calling...' : 'Incoming Call')}
              </CallerName>
              <CallerNumber>
                {formatPhoneDisplay(callerInfo?.number || dialNumber)}
              </CallerNumber>
              <CallTimer>{formatDuration(callDuration)}</CallTimer>

              {showKeypad && (
                <DialPad style={{ marginBottom: 12 }}>
                  {DIAL_KEYS.map(({ digit, letters }) => (
                    <DialKey key={digit} onClick={() => sendDTMF(digit)} style={{ height: 40, fontSize: '1rem' }}>
                      {digit}
                    </DialKey>
                  ))}
                </DialPad>
              )}

              <CallControls>
                <ControlButton
                  active={isMuted}
                  onClick={toggleMute}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                </ControlButton>

                <ControlButton
                  active={showKeypad}
                  onClick={() => setShowKeypad(!showKeypad)}
                  title="Keypad"
                >
                  <FaTh />
                </ControlButton>

                <HangupButton onClick={handleHangup} title="Hang up">
                  <FaPhoneSlash />
                </HangupButton>
              </CallControls>

              {callState === 'connecting' && (
                <div style={{ color: '#F69620', fontSize: '0.85rem', marginTop: 8 }}>
                  Connecting...
                </div>
              )}
            </ActiveCallContainer>
          )}
        </PanelBody>
      </Panel>
      )}
    </WidgetContainer>
  );
};

export default SoftphoneWidget;
